<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'
import { getTranslatedField } from '@/plugins/i18n'
import { useRegistryStore } from '@/stores/registry.store'

import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const i18n = getI18n()
const registryStore = useRegistryStore()

const props = defineProps({
  customerId: Number,
  customerContactId: Number,
  privacyModuleType: Number,
  subject: {
    type: Object,
    required: true
  },
  entityLock: {
    type: Object,
    required: true
  },
})
const formInstance = ref(null)

const subjectRefId = props.subject.id
const entityId = props.privacyModuleType === registryStore.privacyModuleTypes.CUSTOMER
  ? props.customerId
  : props.customerContactId

const gridId = 'customer-privacy-module-grid'
const { gridRef } = dataGrid.useGridRef()

const getCellValue = (rowData) => getTranslatedField(rowData, 'name')

const rowButtons = props.entityLock.getEntityLockButtons({
  visible: {
    delete: (e) => false
  }
})

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  { dataField: 'id', cellTemplate: 'completedTemplate', caption: '', width: '3rem', allowFiltering: false, alignment: 'center' },
  { dataField: 'supplier_name', caption: i18n.t('common.name'), sortOrder: 'asc' },
  { dataField: 'description', caption: i18n.t('common.description'), calculateDisplayValue: getCellValue, sortOrder: 'asc' },
  { dataField: 'options', caption: i18n.t('common.option', 2), visible: false, showInColumnChooser: false },
  { dataField: 'acquisition', caption: i18n.t('common.acquisition'), dataType: 'date' },
  { dataField: 'expiration_date', caption: i18n.t('common.expiration'), dataType: 'date', sortOrder: 'asc'},
]

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  const mainTitle = props.title
  const itemTitle = `${i18n.t('common.privacyModule')} - ${getCellValue(i, 'name')}`
  return defaultDialogTitle(i, `${itemTitle} | ${mainTitle}`)
}

const onInitialized = (e) => { formInstance.value = e.component }

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 2,
  items: [
      {
        name: 'mainInfo',
        itemType: 'group',
        caption: i18n.t('common.mainInfo'),
        colCount: 2,
        items: [
          { dataField: 'supplier_name', colSpan: 2, editorOptions: { readOnly: true } },
          { dataField: 'acquisition', editorOptions: { readOnly: true } },
          { dataField: 'expiration_date', editorOptions: { readOnly: true } },
        ]
      },
  ],
  onInitialized: onInitialized,
}, {
  allowAdding: false,
  allowUpdating: (e) => !props.entityLock?.isLocked(false, subjectRefId),
  allowDeleting: false,
  onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
})

const isEditItemObsolete = ref(false)
const optionItems = ref([])

let lastId = null
let lastPrivacyModuleRef = null
let lastCustomerRef = null
let lastCustomerContactRef = null

const onEditingStart = async(e) => {
  lastId = e.data.id
  lastPrivacyModuleRef = e.data.privacy_module_ref
  lastCustomerRef = lastCustomerContactRef = null

  const params = {
    privacy_module_ref: lastPrivacyModuleRef,
  }
  if (props.privacyModuleType === registryStore.privacyModuleTypes.CUSTOMER) {
    params.customer_ref = e.data.customer_ref || entityId
    lastCustomerRef = params.customer_ref
  } else {
    params.customer_contact_ref = e.data.customer_contact_ref || entityId
    lastCustomerContactRef = params.customer_contact_ref
  }
  isEditItemObsolete.value = e.data?.obsolete

  e.promise = registryStore.api.getCustomerPrivacyModuleFieldsAsync(params).then(res => {
    let items = formInstance.value.option('items')

    const groups = []
    optionItems.value = []

    res.data = res.data.sort((a, b) => {
      if (a.group_order === b.group_order) {
        return a.order - b.order
      }
      return a.group_order - b.group_order
    })

    res.data.forEach(item => {
      if (!groups.some(g => g.id === item.group_id)) {
        groups.push({
          id: item.group_id,
          name: item.group_name,
          order: item.group_order,
          items: []
        })
      }
      const currentGroup = groups.find(g => g.id === item.group_id)
      currentGroup.items.push(item)
      optionItems.value.push({
        id: item.id,
        name: item.name,
        privacy_module_field_ref: item.privacy_module_field_ref,
        type: item.type,
        bool_value: item.bool_value,
        txt_value: item.txt_value,
      })
    })

    items = items.filter(i => i.name !== i18n.t('common.mainInfo'))

    items.push(
      ...groups.map(group => {
        return {
          itemType: 'group',
          caption: group.name || i18n.t('common.otherOption', 2),
          colCount: 2,
          items: group.items.map(item => {
            return {
              id: `option_${item.id}`,
              name: item.name,
              dataField: item.type === 0 ? `options[${item.id}].bool_value` : `options[${item.id}].txt_value`,
              label: { text: item.name },
              editorType: item.type === 0 ? 'dxCheckBox' : 'dxTextBox',
              editorOptions: {
                caption: item.name,
                value: item.type === 0 ? item.bool_value : item.txt_value,
                onInitialized: (args) => { args.element.id = item.id },
                onValueChanged: (args) => {
                  optionItems.value.forEach(i => {
                    if (i.id === +args.element.id) {
                      if (i.type === 0) {
                        i.bool_value = args.value
                      } else {
                        i.txt_value = args.value
                      }
                    }
                  })
                },
              },
            }
          }),
        }
      }))

    formInstance.value.option('items', items)
  })
}

const onRowUpdating = (e) => {
  e.newData.privacy_module_ref = e.oldData.privacy_module_ref
  if (props.privacyModuleType === registryStore.privacyModuleTypes.CUSTOMER) {
    e.newData.customer_ref = entityId
  } else {
    e.newData.customer_contact_ref = entityId
  }
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data' && e.data?.obsolete) { e.rowElement.classList.add('dx-disabled-row') }
}

const dataSource = computed(() => {
  const entityKey = props.privacyModuleType === registryStore.privacyModuleTypes.CUSTOMER ? 'customer_ref' : 'customer_contact_ref'
  return dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.customerPrivacyModulesUrl, { customParamsFn: () => ({ [entityKey]: entityId }) })
})

editing.popup.onContentReady = (e) => {
  e.component.option('toolbarItems[0].disabled', isEditItemObsolete.value)
}
editing.popup.toolbarItems = [
  { widget: 'dxButton', location: 'after', toolbar: 'bottom',
    options: {
      text: i18n.t('common.save'),
      onClick: async(e) => {
        const data = {
          privacy_module_ref: lastPrivacyModuleRef,
          options: optionItems.value,
        }
        if (props.privacyModuleType === registryStore.privacyModuleTypes.CUSTOMER) {
          data.customer_ref = lastCustomerRef
        } else {
          data.customer_contact_ref = lastCustomerContactRef
        }

        await registryStore.updateCustomerPrivacyModuleAsync(lastId, data)
        gridRef.value.instance.saveEditData()
        gridRef.value.instance.refresh()
      }
    }
  },
  {
    widget: 'dxButton', location: 'after', toolbar: 'bottom',
    options: {
      text: i18n.t('common.cancel'),
      onClick: () => gridRef.value.instance.cancelEditData()
    }
  }
]

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: dataSource.value,
  columns: columns,
  editing: editing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  filterRow: { visible: false },
  onEditingStart: onEditingStart,
  onRowUpdating: onRowUpdating,
  onRowPrepared: onRowPrepared,
  height: '100%',
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #completedTemplate="{ data: cellInfo }">
        <q-icon
          v-if="cellInfo.row.data.is_completed"
          name="mdi-check-circle"
          color="green"
          size="1.25rem"
        />
        <q-icon
          v-if="cellInfo.row.data.obsolete"
          name="mdi-calendar-remove"
          color="grey"
          size="1.25rem"
        >
          <q-tooltip>
            {{ i18n.t('common.obsolete') }}
          </q-tooltip>
        </q-icon>
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Subject'"
          :master-entity-id="subjectRefId"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
<style>
</style>
